import { z, ZodTypeAny } from 'zod';

import { FieldFilling } from '../../types/interfaces';
import {
  getFieldNameSchema,
  getFieldNameTranslate
} from '../../utils/getFieldName';

interface IExecutorsSchema {
  id: string | number;
  fullName?: string;
  label?: string;
}


const ExecutorsSchema: z.ZodSchema<IExecutorsSchema> = z.lazy(() =>
  z.object({
    id: z.string().or(z.number()),
    fullName: z.string().optional(),
    label: z.string().optional()
  })
);

const parseDate = (dateStr: string | undefined): Date | null => {
  if (dateStr === undefined) {
    return null;
  }
  const date = new Date(dateStr);
  return isNaN(date.getTime()) ? null : date;
};

const isAfter = (date1: Date | null, date2: Date | null): boolean => {
  return !!(date1 && date2 && date1 > date2);
};

const isBefore = (date1: Date | null, date2: Date | null): boolean => {
  return !!(date1 && date2 && date1 < date2);
};

const selectionSchema = z
  .object({
    key: z.string().or(z.number()),
    label: z.string()
  })
  .or(z.string())
  .optional();

const coreTaskSchema = z.object({
  id: z.number(),
  externalId: z.string().max(50, { message: 'До 50 символов' }),
  name: z.string().max(200, { message: 'До 200 символов' }),
  taskType: z.object({
    key: z.string().min(1, { message: 'Key is required' }).or(z.number()),
    label: z.string().min(1, { message: 'Label is required' })
  }),
  taskStatus: selectionSchema,
  asset: selectionSchema,
  department: selectionSchema,
  employees: z.array(ExecutorsSchema).optional(),
  actualStartDate: z.string().optional(),
  plannedStartDate: z.string().optional(),
  plannedEndDate: z.string().optional(),
  actualEndDate: z.string().optional(),
  priority: z.string().or(z.number()).optional(),
  description: z.string().max(300, { message: 'Не более 300 символов' }),
  plannedOperations: z.array(z.any()).optional(),
  actualOperations: z.array(z.any()).optional(),
});

export const createTaskSchemaWithDynamicFields = (
  fieldFillingControls: FieldFilling[],
  isInterval: boolean
) => {
  const schemaShape = coreTaskSchema.shape;

  const dynamicSchemaShape: Record<string, z.ZodTypeAny> = { ...schemaShape };

  fieldFillingControls.forEach((control) => {
    const { isRequired, fieldType } = control;
    const fieldName = getFieldNameSchema(fieldType);
    const translatedName = getFieldNameTranslate(fieldName);

    if (!fieldName) return;

    let updatedFieldSchema: ZodTypeAny;

    switch (fieldType) {
    case 1: // Name
    case 9: // Description
      if (isRequired) {
        updatedFieldSchema = z
          .string()
          .min(1, `Пожалуйста, ${translatedName}`)
          .refine((value) => value.trim().length > 0, {
            message: 'Поле не может содержать только пробелы'
          });
      } else {
        updatedFieldSchema = z
          .string()
          .optional()
          .refine(
            (value) => value === '' || (value && value.trim().length > 0),
            {
              message: 'Поле не может содержать только пробелы'
            }
          );
      }
      break;
    case 2: // Task Type
      updatedFieldSchema = z.object({
        key: z.string().or(z.number()),
        label: z.string()
      });
      break;
    case 3: // Asset
    case 4: // Department
      updatedFieldSchema = z.object({
        key: z.union([z.string(), z.number()]),
        label: z.string()
      });
      if (!isRequired) {
        updatedFieldSchema = updatedFieldSchema.optional();
      } else {
        // When required, make sure the whole object triggers a single error
        updatedFieldSchema = updatedFieldSchema.refine(
          (obj) =>
            obj &&
              obj.key &&
              obj.key.toString().trim().length > 0 &&
              obj.label.trim().length > 0,
          {
            message: `Пожалуйста, укажите ${translatedName}`
          }
        );
      }
      break;
    case 5: // Employees
      updatedFieldSchema = z.array(ExecutorsSchema);
      if (isRequired) {
        updatedFieldSchema = z
          .array(ExecutorsSchema)
          .min(1, {
            message: 'Данному наряду должен быть назначен исполнитель'
          });
      }
      break;
    case 12: { // plannedOperations
      const plannedOpsArray = z.array(
        z.object({
          key: z.union([z.string(), z.number()]),
          label: z.string()
        })
      );
      updatedFieldSchema = isRequired
        ? plannedOpsArray.min(1, {
          message: `Пожалуйста, ${translatedName}`
        })
        : plannedOpsArray;
      break;
    }
    case 13: { // actualOperations
      const actualOpsArray = z.array(z.any());
      updatedFieldSchema = isRequired
        ? actualOpsArray.min(1, {
          message: `Пожалуйста, ${translatedName}`
        })
        : actualOpsArray;
      break;
    }
    case 14: { // interval
      if (isRequired && isInterval) {
        updatedFieldSchema = z
          .union([
            z.object({
              start: z.string().min(1, 'Укажите дату начала'),
              end: z.string().min(1, 'Укажите дату окончания'),
              intervalOccupancy: z.number().optional()
            }),
            z.null(),
            z.undefined()
          ])
          .superRefine((data, ctx) => {
            if (data === null || data === undefined) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Пожалуйста, укажите интервал`,
              });
              return;
            }
          });
      } else {
        updatedFieldSchema = z
          .union([
            z.object({
              start: z.string(),
              end: z.string(),
              intervalOccupancy: z.number().optional()
            }),
            z.null(),
            z.undefined()
          ])
          .default(null);
      }
      break;
    }
    case 6: // Actual Start Date
    case 7: // Planned End Date
    case 8: // Actual End Date
    case 11: // Planned Start Date
      if (isRequired) {
        updatedFieldSchema = z.string().refine(
          (data) => {
            return typeof data !== 'undefined' && data.trim().length > 0;
          },
          {
            message: `Пожалуйста, укажите ${translatedName}` // Custom message
          }
        );
      } else {
        updatedFieldSchema = z.union([z.string(), z.undefined()]);
      }
      break;
    default:
      updatedFieldSchema = z
        .string()
        .optional()
        .refine((value) => !value || value.trim().length > 0, {
          message: `${translatedName} не может содержать только пробелы`
        }); // Fallback case for unknown field types
      break;
    }

    if (isRequired && updatedFieldSchema instanceof z.ZodString) {
      dynamicSchemaShape[fieldName] = updatedFieldSchema.min(
        1,
        `Пожалуйста, ${translatedName}`
      );
    } else if (isRequired && [5, 12, 13, 14].includes(fieldType)) {
      dynamicSchemaShape[fieldName] = updatedFieldSchema;
    } else {
      dynamicSchemaShape[fieldName] = updatedFieldSchema.optional();
    }
  });

  // Create a new schema from the modified shape
  return z.object(dynamicSchemaShape);
};

// Apply refinements to the schema
export const applyRefinements = (schema: z.ZodObject<any>) => {
  return schema
    .refine((data) => data.taskType.key !== '', {
      message: 'Выберите тип наряда.',
      path: ['taskType']
    })
    .refine(
      (data) => {
        const plannedStartDate = parseDate(data.plannedStartDate);
        const plannedEndDate = parseDate(data.plannedEndDate);

        if (
          plannedStartDate &&
          plannedEndDate &&
          isAfter(plannedStartDate, plannedEndDate)
        ) {
          return false;
        }
        return true;
      },
      {
        message:
          'Плановая дата и время начала не может быть позже плановой даты окончания.',
        path: ['plannedStartDate']
      }
    )
    .refine(
      (data) => {
        const plannedStartDate = parseDate(data.plannedStartDate);
        const plannedEndDate = parseDate(data.plannedEndDate);

        if (
          plannedEndDate &&
          plannedStartDate &&
          isBefore(plannedEndDate, plannedStartDate)
        ) {
          return false;
        }
        return true;
      },
      {
        message:
          'Плановая дата и время окончания не может быть раньше плановой даты начала.',
        path: ['plannedEndDate']
      }
    )
    .refine(
      (data) => {
        const actualStartDate = parseDate(data.actualStartDate);
        const actualEndDate = parseDate(data.actualEndDate);

        if (
          actualStartDate &&
          actualEndDate &&
          isAfter(actualStartDate, actualEndDate)
        ) {
          return false;
        }
        return true;
      },
      {
        message: 'Дата и время начала не может быть позже даты окончания.',
        path: ['actualStartDate']
      }
    )
    .refine(
      (data) => {
        const actualStartDate = parseDate(data.actualStartDate);
        const actualEndDate = parseDate(data.actualEndDate);

        if (
          actualEndDate &&
          actualStartDate &&
          isBefore(actualEndDate, actualStartDate)
        ) {
          return false;
        }
        return true;
      },
      {
        message: 'Дата и время окончания не может быть раньше даты начала.',
        path: ['actualEndDate']
      }
    );
};

// Generate schema with dynamic fields and apply refinements
export const createTaskSchemaWithDynamicFieldsAndRefinements = (
  fieldFillingControls: FieldFilling[],
  isInterval: boolean = false
) => {
  const dynamicSchema = createTaskSchemaWithDynamicFields(fieldFillingControls, isInterval);
  return applyRefinements(dynamicSchema);
};
